import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronRightIcon,
  GlobeAltIcon,
  PlusIcon,
  ServerStackIcon,
  ServerIcon,
  Squares2X2Icon,
  UserCircleIcon,
  LifebuoyIcon,
  UsersIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/lib/auth'

type ChildItem = {
  name: string
  href: string
  matches?: (pathname: string) => boolean
}

type NavGroup = {
  key: string
  title: string
  name: string
  href: string
  icon: React.ElementType
  matches: (pathname: string) => boolean
  children: ChildItem[]
}

const groups: NavGroup[] = [
  {
    key: 'game-servers',
    title: 'HOSTING',
    name: 'Servers',
    href: '/servers',
    icon: ServerStackIcon,
    matches: pathname => pathname.startsWith('/servers') || pathname.startsWith('/game-servers') || pathname.startsWith('/compute'),
    children: [
      { name: 'All servers', href: '/servers', matches: pathname => pathname.startsWith('/servers') },
      { name: 'Minecraft Networks', href: '/game-servers/networks', matches: pathname => pathname.startsWith('/game-servers/networks') },
    ],
  },
  {
    key: 'account',
    title: 'ACCOUNT',
    name: 'Account',
    href: '/account',
    icon: UserCircleIcon,
    matches: pathname => pathname.startsWith('/account') || pathname.startsWith('/billing-'),
    children: [
      { name: 'Profile', href: '/account/profile' },
      { name: 'Billing', href: '/billing-overview', matches: pathname => pathname.startsWith('/billing-') },
      { name: 'Backup Vault', href: '/account/backups' },
      { name: 'Activity', href: '/account/activity' },
    ],
  },
]

const storageKey = 'metrly.console.navigation.expanded'
const defaultExpanded = Object.fromEntries(groups.map(group => [group.key, true]))

export default function ConsoleSidebarNav({ onNavigate, workspaceV2 = false }: { onNavigate?: () => void; workspaceV2?: boolean }) {
  const { pathname } = useLocation()
  const user = useAuthStore(state => state.user)
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey)
      return stored ? { ...defaultExpanded, ...JSON.parse(stored) } : defaultExpanded
    } catch {
      return defaultExpanded
    }
  })

  useEffect(() => {
    const current = groups.find(group => group.matches(pathname))
    if (current) setExpanded(previous => previous[current.key] ? previous : { ...previous, [current.key]: true })
  }, [pathname])

  useEffect(() => {
    try { window.localStorage.setItem(storageKey, JSON.stringify(expanded)) } catch { /* storage is optional */ }
  }, [expanded])

  const parentBase = workspaceV2 ? 'text-slate-300 hover:bg-white/5 hover:text-white' : 'text-white/80 hover:bg-white/5 hover:text-white'
  const parentCurrent = workspaceV2 ? 'bg-violet-500/12 font-semibold text-violet-100' : 'bg-white/10 font-semibold text-white'
  const childBase = workspaceV2 ? 'text-slate-400 hover:bg-white/5 hover:text-slate-100' : 'text-white/75 hover:bg-white/5 hover:text-white'
  const childCurrent = workspaceV2 ? 'bg-violet-500/12 font-semibold text-violet-100' : 'bg-white/10 font-semibold text-white'

  if (user?.role === 'sre') {
    const links = [
      { name: 'Tickets', href: '/admin/support', icon: LifebuoyIcon, exact: true },
      { name: 'Customers', href: '/admin/support/customers', icon: UsersIcon },
      { name: 'Servers', href: '/admin/support/servers', icon: ServerStackIcon },
      { name: 'Profile', href: '/account/profile', icon: UserCircleIcon },
    ]
    return <nav className="mt-4 space-y-2 px-3" aria-label="Support navigation">
      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">Support workspace</div>
      {links.map(item => {
        const active = item.exact ? pathname === item.href : pathname.startsWith(item.href)
        return <Link key={item.href} to={item.href} onClick={onNavigate} className={`flex min-h-10 items-center gap-3 rounded-md border-l-[3px] px-3 py-1.5 text-[15px] transition ${active ? 'border-white bg-white/10 font-semibold text-white' : `border-transparent font-medium ${parentBase}`}`}>
          <item.icon className="h-[19px] w-[19px] shrink-0" />{item.name}
        </Link>
      })}
    </nav>
  }

  return (
    <nav className="mt-4 space-y-5 px-3" aria-label="Primary navigation">
      <Link
        to="/"
        onClick={onNavigate}
        className={`flex min-h-10 items-center gap-3 rounded-md border-l-[3px] px-3 py-1.5 text-[15px] transition ${
          pathname === '/'
            ? 'border-white bg-white/10 font-semibold text-white'
            : `border-transparent font-medium ${parentBase}`
        }`}
      >
        <Squares2X2Icon className="h-[19px] w-[19px] shrink-0" />
        Dashboard
      </Link>

      <Link
        to="/compute/deploy"
        onClick={onNavigate}
        title="Create server"
        aria-label="Create server"
        className="mt-3 flex min-h-10 items-center gap-3 rounded-md border border-white/20 bg-white/15 px-3 py-1.5 text-[15px] font-semibold text-white shadow-sm transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
      >
        <span className="relative shrink-0">
          <ServerIcon className="h-[19px] w-[19px]" />
          <PlusIcon className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-primary-700 stroke-[3]" />
        </span>
        <span className="truncate">Create server</span>
      </Link>

      {groups.slice(0, 1).map(group => {
        const isExpanded = expanded[group.key]
        const isCurrent = group.matches(pathname)
        return (
          <div key={group.key}>
            <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">{group.title}</div>
            <div className={`flex min-h-10 items-center rounded-md transition ${isCurrent ? parentCurrent : parentBase}`}>
              <Link
                to={group.href}
                onClick={() => {
                  setExpanded(previous => ({ ...previous, [group.key]: true }))
                  onNavigate?.()
                }}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-1.5 text-[15px] font-medium"
              >
                <group.icon className="h-[19px] w-[19px] shrink-0" />
                <span className="truncate">{group.name}</span>
              </Link>
              <button
                type="button"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.name}`}
                aria-expanded={isExpanded}
                onClick={() => setExpanded(previous => ({ ...previous, [group.key]: !previous[group.key] }))}
                className="mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-white/10"
              >
                <ChevronRightIcon className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
              </button>
            </div>

            <div
              className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-in-out ${isExpanded ? 'mt-1 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'}`}
              aria-hidden={!isExpanded}
            >
              <div className="overflow-hidden">
                <div className="ml-[21px] space-y-0.5 border-l border-white/25 pl-3">
                  {group.children.map(child => {
                    const childIsCurrent = child.matches ? child.matches(pathname) : pathname === child.href
                    const className = `block min-h-8 min-w-0 truncate rounded-md px-2 py-1.5 text-sm transition ${childIsCurrent ? childCurrent : childBase}`
                    return <Link key={child.href} to={child.href} onClick={onNavigate} tabIndex={isExpanded ? undefined : -1} className={className} aria-current={childIsCurrent ? 'page' : undefined} title={child.name}>{child.name}</Link>
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      })}

      <div>
        <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">NETWORK</div>
        <Link
          to="/dns/subdomains"
          onClick={onNavigate}
          className={`flex min-h-10 items-center gap-3 rounded-md border-l-[3px] px-3 py-1.5 text-[15px] transition ${
            pathname.startsWith('/dns')
              ? 'border-white bg-white/10 font-semibold text-white'
              : `border-transparent font-medium ${parentBase}`
          }`}
          aria-current={pathname.startsWith('/dns') ? 'page' : undefined}
        >
          <GlobeAltIcon className="h-[19px] w-[19px] shrink-0" />
          Subdomains
        </Link>
      </div>

      {groups.slice(1).map(group => {
        const isExpanded = expanded[group.key]
        const isCurrent = group.matches(pathname)
        return (
          <div key={group.key}>
            <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">{group.title}</div>
            <div className={`flex min-h-10 items-center rounded-md transition ${isCurrent ? parentCurrent : parentBase}`}>
              <Link
                to={group.href}
                onClick={() => {
                  setExpanded(previous => ({ ...previous, [group.key]: true }))
                  onNavigate?.()
                }}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-1.5 text-[15px] font-medium"
              >
                <group.icon className="h-[19px] w-[19px] shrink-0" />
                <span className="truncate">{group.name}</span>
              </Link>
              <button
                type="button"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${group.name}`}
                aria-expanded={isExpanded}
                onClick={() => setExpanded(previous => ({ ...previous, [group.key]: !previous[group.key] }))}
                className="mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-white/10"
              >
                <ChevronRightIcon className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
              </button>
            </div>

            <div
              className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-in-out ${isExpanded ? 'mt-1 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'}`}
              aria-hidden={!isExpanded}
            >
              <div className="overflow-hidden">
                <div className="ml-[21px] space-y-0.5 border-l border-white/25 pl-3">
                  {group.children.map(child => {
                    const childIsCurrent = child.matches ? child.matches(pathname) : pathname === child.href
                    const className = `block min-h-8 min-w-0 truncate rounded-md px-2 py-1.5 text-sm transition ${childIsCurrent ? childCurrent : childBase}`
                    return <Link key={child.href} to={child.href} onClick={onNavigate} tabIndex={isExpanded ? undefined : -1} className={className} aria-current={childIsCurrent ? 'page' : undefined} title={child.name}>{child.name}</Link>
                  })}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </nav>
  )
}
